import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignIn,
  fetchUserAttributes,
} from 'aws-amplify/auth';

interface AuthUser {
  username: string;
  userId: string;
  email: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresNewPassword: boolean;
  login: (email: string, password: string) => Promise<{ requiresNewPassword: boolean }>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requiresNewPassword, setRequiresNewPassword] = useState(false);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchAndSetUser(): Promise<void> {
    const currentUser = await getCurrentUser();
    let email = currentUser.username;
    try {
      const attributes = await fetchUserAttributes();
      email = attributes.email ?? currentUser.username;
    } catch {
      // 属性取得失敗時はユーザー名でフォールバック
    }
    setUser({ username: currentUser.username, userId: currentUser.userId, email });
  }

  async function checkAuth(): Promise<void> {
    // mock モードでは Cognito をスキップしてダミーユーザーで自動認証
    if (import.meta.env.VITE_API_MODE === 'mock') {
      console.warn('[AuthContext] Mock mode: authentication bypassed (dev only)');
      setUser({ username: 'dev@example.com', userId: 'mock-user-id', email: 'dev@example.com' });
      setIsLoading(false);
      return;
    }
    try {
      await fetchAndSetUser();
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }

  const login = useCallback(
    async (email: string, password: string): Promise<{ requiresNewPassword: boolean }> => {
      const result = await signIn({ username: email, password });

      // 初回ログイン時のパスワード変更チャレンジ
      if (
        !result.isSignedIn &&
        result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        setRequiresNewPassword(true);
        return { requiresNewPassword: true };
      }

      await fetchAndSetUser();
      return { requiresNewPassword: false };
    },
    [],
  );

  const completeNewPassword = useCallback(async (newPassword: string): Promise<void> => {
    await confirmSignIn({ challengeResponse: newPassword });
    setRequiresNewPassword(false);
    await fetchAndSetUser();
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await signOut();
    setUser(null);
  }, []);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    requiresNewPassword,
    login,
    completeNewPassword,
    logout,
    getIdToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
